import { CommonModule, DatePipe } from '@angular/common';
import { Component, inject } from '@angular/core';
import { RouterModule } from '@angular/router';
import { ClaimViewResponse } from '../../../models/api/view/claim-view/claim-view-response';
import { SearchInput } from '../../../models/api/view/search/search-input';
import { ExportService } from '../../../services/export/export.service';
import { ClaimViewColumnService } from '../../../services/view/claim-view-column.service';
import { WatchlistService } from '../../../services/view/watchlist.service';
import { ClaimExportMapper } from '../../../utilities/claim-export-mapper';
import { BaseComponent } from '../../base/base.component';
import { ExportFormat } from '../../core/buttons/export-button.component';
import { ApiErrorLabelComponent } from '../../core/labels/api-error-label.component';
import { EmptyStateComponent } from '../../core/panels/empty-state.component';
import {
  LoadingPanelComponent,
  LoadingState,
} from '../../core/panels/loading-panel.component';
import { ClaimViewComponent } from '../claim-view/claim-view.component';
import { SearchFormComponent } from './search-form.component';

@Component({
  selector: 'app-search',
  imports: [
    CommonModule,
    LoadingPanelComponent,
    RouterModule,
    EmptyStateComponent,
    SearchFormComponent,
    ClaimViewComponent,
    ApiErrorLabelComponent,
  ],
  template: `
    <div class="tw-w-full tw-pb-4">
      <app-search-form
        #form
        (search)="search($event)"
        (clear)="clear()"
        [loading]="state === 'Loading'"
      ></app-search-form>
    </div>

    <app-loading-panel
      id="search-results"
      [state]="state"
      loadingStyle="Skeleton"
      [loadingHeight]="504"
    >
      <ng-template #init>
        <app-empty-state
          icon="fa-light fa-search"
          size="120px"
          label="Search results"
          message="Search results will appear here"
          height="400px"
        />
      </ng-template>
      <ng-template #empty>
        <app-api-error-label [error]="error" />
        <app-empty-state
          icon="fa-light fa-magnifying-glass"
          size="120px"
          label="No results found"
          message="Try searching with different criteria"
          height="400px"
        />
      </ng-template>
      <ng-template #loaded>
        <app-claim-view
          [claimView]="claimView"
          [columnView]="claimViewColumnService.columnView()"
          [persistPaginationSettings]="true"
          [tableId]="'search-results'"
          sortField="fileNumber"
          [sortOrder]="1"
          (export)="onExport($event)"
          (columnsUpdated)="onColumnsUpdated()"
          (paginationReady)="scrollToResults(true)"
        />
      </ng-template>
    </app-loading-panel>
  `,
  styles: ``,
})
export class SearchComponent extends BaseComponent {
  exportService = inject(ExportService);
  claimViewColumnService = inject(ClaimViewColumnService);
  private watchlist = inject(WatchlistService);
  private datePipe = inject(DatePipe);

  claimView: ClaimViewResponse;

  state: LoadingState = 'Init';

  error: any;

  private lastSearchInput: SearchInput | null = null;

  /** Set before column-layout refresh searches; checked only in scrollToResults(). */
  private skipScroll = false;

  constructor() {
    super();
    this.dispose.subs =
      this.claimViewColumnService.columnConfigRefreshed$.subscribe(() => {
        if (this.lastSearchInput) {
          this.skipScroll = true;
          this.search(this.lastSearchInput);
        }
      });
  }

  search(searchInput: SearchInput): void {
    const { trySwappedNameOrderIfEmpty, ...payload } = searchInput;
    this.state = 'Loading';
    this.error = null;
    this.lastSearchInput = payload;

    if (trySwappedNameOrderIfEmpty) {
      void this.searchWithOptionalNameSwap(payload);
      return;
    }

    Promise.all([
      this.api.search.post(payload),
      this.claimViewColumnService.load(),
    ])
      .then((value) => {
        this.claimView = value[0];
        this.state = this.claimView.rows.length ? 'Loaded' : 'Empty';
        this.scrollToResults();
      })
      .catch((error) => {
        this.error = error;
        this.state = 'Empty';
        this.scrollToResults();
      });
  }

  /**
   * Two-token quick full-name uses First Last; many records are entered or searched as Last First.
   * If the first query returns nothing, retry with first/last swapped (single-word tokens only).
   */
  private async searchWithOptionalNameSwap(
    payload: SearchInput,
  ): Promise<void> {
    try {
      const [firstView] = await Promise.all([
        this.api.search.post(payload),
        this.claimViewColumnService.load(),
      ]);

      const fn = payload.firstName?.trim() ?? '';
      const ln = payload.lastName?.trim() ?? '';
      const canSwap =
        firstView.rows.length === 0 &&
        fn.length > 0 &&
        ln.length > 0 &&
        !/\s/.test(fn) &&
        !/\s/.test(ln);

      if (canSwap) {
        const swapped: SearchInput = {
          ...payload,
          firstName: ln,
          lastName: fn,
        };
        const [retryView] = await Promise.all([
          this.api.search.post(swapped),
          this.claimViewColumnService.load(),
        ]);
        this.claimView = retryView;
        this.state = retryView.rows.length ? 'Loaded' : 'Empty';
        this.lastSearchInput = swapped;
      } else {
        this.claimView = firstView;
        this.state = firstView.rows.length ? 'Loaded' : 'Empty';
      }

      this.scrollToResults();
    } catch (error) {
      this.error = error;
      this.state = 'Empty';
      this.scrollToResults();
    }
  }

  /**
   * Scroll to the results panel after search.
   * @param gridReady Pass true when the table emits paginationReady (Loaded results only).
   */
  scrollToResults(gridReady = false): void {
    const loaded = this.state === 'Loaded';
    const deferScroll = loaded ? !gridReady : gridReady;

    if (this.skipScroll) {
      this.skipScroll = loaded && !gridReady;
      return;
    }

    if (deferScroll) {
      return;
    }

    this.dispose.timeouts = setTimeout(() => {
      document.getElementById('search-results')?.scrollIntoView({
        behavior: 'smooth',
        block: 'start',
      });
    });
  }

  async clear() {
    this.skipScroll = false;
    this.state = 'Init';
    this.claimView = null;
    this.lastSearchInput = null;
  }

  onColumnsUpdated() {
    if (this.lastSearchInput) {
      this.skipScroll = true;
      this.search(this.lastSearchInput);
    }
  }

  async onExport(format: ExportFormat) {
    try {
      const claimRows = this.claimView?.rows ?? [];
      const watchedClaims = this.watchlist.claims()?.rows ?? [];
      const rows = claimRows.map((row) => ({
        ...row,
        watchStatus: watchedClaims.some(
          (w) =>
            w.recordId === row.recordId && w.recordSource === row.recordSource,
        )
          ? 'Y'
          : 'N',
      }));
      const claimColumns =
        this.claimViewColumnService.columnView()?.columns ?? [];
      const columns = [
        {
          propertyName: 'watchStatus',
          headerText: 'Watchlist',
        },
        ...claimColumns.filter((col) => col.propertyName !== 'DateAccessed'),
      ];
      const formattedDate = this.datePipe.transform(new Date(), 'yyyyMMddHHmm');
      const filename = `ClaimSearch_${formattedDate}.${format}`;

      if (format === 'csv') {
        const csvData = ClaimExportMapper.toCsvExport(
          rows,
          columns,
          'ClaimSearch',
        );
        await this.exportService.exportCsv(csvData, filename);
      } else if (format === 'xlsx') {
        const excelData = ClaimExportMapper.toExcelExport(
          rows,
          columns,
          'ClaimSearch',
          'OmniInsights Export',
        );
        await this.exportService.exportExcel(excelData, filename);
      }
    } catch (error) {
      this.error = error;
    }
  }
}
